// Monolith bridge helpers: map the monolith's model-based AI request to a
// commerce AiOp, and normalise the Edge-Function `result` (fal.ai for image ops,
// OpenRouter chat-completions for text/vision) into the monolith's expected
// `{ imageUrl, textContent }` shape.
//
// Pure functions — no SDK / DOM. Unit-tested. This is the contract that lets the
// monolith's callOpenRouterAI() delegate to window.JoeFlowCommerce without the
// callers (inpaint/texture/analyze/critic/anatomy) changing their result handling.

import type { AiOp, AiProviderTier } from '../domain/types';

export interface MonolithAiRequest {
  model: string;
  prompt?: string;
  imageDataUrl?: string;
  textOnly?: boolean;
  /** Provider tier for image ops: 'local' | 'standard' | 'premium'. Default 'standard'. */
  quality?: AiProviderTier;
}

export interface MonolithAiResult {
  imageUrl: string | null;
  textContent: string | null;
}

/** Map the monolith's {model, textOnly, imageDataUrl} to a server AiOp. */
export function mapModelToOp(req: MonolithAiRequest): AiOp {
  if (req.textOnly) return 'idea';
  // Image-generation models carry "image" in the id (e.g. gemini-2.5-flash-image).
  if (/image/i.test(req.model)) return 'transform';
  // A non-image model with a canvas attached = vision analysis (may return text
  // and/or an overlay image). Without an image it is a pure text "idea" prompt.
  if (req.imageDataUrl) return 'vision';
  return 'idea';
}

function isValidBase64Image(s: string): boolean {
  // Loose check: a plausible base64 blob (the monolith applies the same guard).
  return s.length > 16 && /^[A-Za-z0-9+/=\s]+$/.test(s);
}

function normalizeImageUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url; // remote URL (fal.ai hosted) — usable directly
  if (isValidBase64Image(url)) return 'data:image/png;base64,' + url;
  return null;
}

interface FalLikeImage {
  url?: string;
}
interface FalLikeResult {
  images?: FalLikeImage[];
  image?: { url?: string };
}
interface OpenRouterContentPart {
  type?: string;
  text?: string;
  image_url?: { url?: string };
}
interface OpenRouterMessage {
  content?: string | OpenRouterContentPart[];
  images?: Array<string | { image_url?: { url?: string } }>;
}
interface OpenRouterResult {
  choices?: Array<{ message?: OpenRouterMessage }>;
}

/** Extract {imageUrl, textContent} from an OpenRouter chat-completion result. */
function fromOpenRouter(result: OpenRouterResult): MonolithAiResult {
  const choice = result.choices && result.choices[0] ? result.choices[0].message : undefined;
  let imageUrl: string | null = null;
  let textContent: string | null = null;
  if (!choice) return { imageUrl: null, textContent: null };

  const content = choice.content;
  if (Array.isArray(content)) {
    const textParts = content.filter((p) => p.type === 'text' && typeof p.text === 'string');
    if (textParts.length > 0) textContent = textParts.map((p) => p.text).join('\n');
    const imgPart = content.find((p) => p.type === 'image_url');
    if (imgPart && imgPart.image_url) imageUrl = imgPart.image_url.url ?? null;
  } else if (typeof content === 'string') {
    const b64Match = content.match(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/);
    if (b64Match) {
      imageUrl = b64Match[0];
      textContent = content.replace(b64Match[0], '').trim() || null;
    } else {
      textContent = content;
    }
  }

  if (!imageUrl && choice.images && choice.images.length > 0) {
    const img = choice.images[0]!;
    imageUrl = typeof img === 'string' ? img : (img.image_url?.url ?? null);
  }

  return { imageUrl: normalizeImageUrl(imageUrl), textContent };
}

/** Extract {imageUrl} from a fal.ai-style image result. */
function fromFal(result: FalLikeResult): MonolithAiResult {
  const url =
    (result.images && result.images[0] ? result.images[0].url : undefined) ??
    (result.image ? result.image.url : undefined) ??
    null;
  return { imageUrl: normalizeImageUrl(url), textContent: null };
}

/**
 * Normalise the Edge-Function `result` for an op into the monolith's shape.
 * Image ops (transform/upscale) come from fal.ai; text/vision from OpenRouter.
 * Falls back across both shapes so a server provider swap can't break callers.
 */
export function normalizeAiResult(op: AiOp, result: unknown): MonolithAiResult {
  if (result == null || typeof result !== 'object') {
    return { imageUrl: null, textContent: null };
  }
  const r = result as OpenRouterResult & FalLikeResult;

  // Prefer the expected shape for the op, then fall back to the other.
  if (op === 'transform' || op === 'upscale') {
    const fal = fromFal(r);
    if (fal.imageUrl) return fal;
    return fromOpenRouter(r);
  }
  const or = fromOpenRouter(r);
  if (or.imageUrl || or.textContent) return or;
  return fromFal(r);
}
